import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Globe, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { useI18n } from "../lib/i18n";

const WA_PHONE = "18196490166";

const LangToggle = ({ mobile = false, inverted = false, lang, setLang }) => (
  <div
    className={`inline-flex items-center border rounded-sm overflow-hidden transition-colors ${
      inverted ? "border-white/25" : "border-[#E2E8F0]"
    } ${mobile ? "" : "ml-1"}`}
    data-testid="lang-toggle"
  >
    <Globe size={13} className={`ml-2 ${inverted ? "text-white/80" : "text-[#1A4E72]"}`} />
    {["fr", "en"].map((l) => (
      <button
        key={l}
        onClick={() => setLang(l)}
        data-testid={`lang-${l}`}
        className={`px-2.5 py-1.5 font-body text-[10px] font-bold uppercase tracking-[0.2em] transition-colors ${
          lang === l
            ? "bg-[#E87B3A] text-white"
            : inverted
            ? "text-white/80 hover:bg-white/10"
            : "text-[#1A4E72] hover:bg-[#F1F5F9]"
        }`}
      >
        {l}
      </button>
    ))}
  </div>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { lang, setLang, t } = useI18n();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isDarkHero = pathname === "/";
  const inverted = isDarkHero && !scrolled;

  const links = [
    { to: "/", label: t("nav.home"), testid: "nav-home" },
    { to: "/catalogue", label: t("nav.catalog"), testid: "nav-catalog" },
    { to: "/contact", label: t("nav.contact"), testid: "nav-contact" },
  ];

  const waHref = `https://wa.me/${WA_PHONE}?text=${encodeURIComponent(t("wa.message"))}`;

  return (
    <motion.header
      data-surface={inverted ? "dark" : "light"}
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/90 backdrop-blur-2xl border-b border-[#E2E8F0] shadow-[0_10px_40px_-20px_rgba(15,23,42,0.15)]"
          : inverted
          ? "bg-[#0B1620]/40 backdrop-blur-md border-b border-white/10"
          : "bg-white/85 backdrop-blur border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="nav-logo-link">
          <Logo size={42} withText inverted={inverted} />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.testid}
              className={({ isActive }) =>
                `relative font-body text-[12px] font-bold uppercase tracking-[0.24em] transition-colors ${
                  isActive
                    ? "text-[#E87B3A]"
                    : inverted
                    ? "text-white/95 hover:text-[#E87B3A]"
                    : "text-[#0F172A] hover:text-[#E87B3A]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <motion.span
                      layoutId="navActive"
                      className="absolute -bottom-1.5 left-0 right-0 h-[2px] bg-[#E87B3A]"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LangToggle inverted={inverted} lang={lang} setLang={setLang} />
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-wa"
            aria-label="WhatsApp"
            className={`inline-flex items-center justify-center w-10 h-10 rounded-sm transition-colors ${
              inverted ? "border border-white/25 text-white hover:bg-[#25D366] hover:border-[#25D366]" : "border border-[#E2E8F0] text-[#128C7E] hover:bg-[#25D366] hover:text-white hover:border-[#25D366]"
            }`}
          >
            <MessageCircle size={16} />
          </a>
          <a
            href="tel:8193503027"
            data-testid="nav-cta-phone"
            className="group inline-flex items-center gap-2 bg-[#E87B3A] hover:bg-[#F08A47] text-white pl-4 pr-3 py-2.5 rounded-sm font-body font-bold text-[11px] uppercase tracking-[0.2em] transition-all hover:-translate-y-[1px]"
          >
            <Phone size={14} /> 819-350-3027
            <span className="w-6 h-6 flex items-center justify-center bg-white/15 group-hover:translate-x-0.5 transition-transform">
              →
            </span>
          </a>
        </div>

        <button
          className={`lg:hidden p-2 ${inverted ? "text-white" : "text-[#0F172A]"}`}
          onClick={() => setOpen(!open)}
          aria-label="Menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden border-t border-[#E2E8F0] bg-white overflow-hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`${l.testid}-mobile`}
                    className={({ isActive }) =>
                      `font-display text-3xl uppercase tracking-wide ${isActive ? "text-[#E87B3A]" : "text-[#0F172A]"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="pt-4 flex items-center justify-between gap-3">
                <LangToggle mobile inverted={false} lang={lang} setLang={setLang} />
              </div>
              <div className="flex flex-col gap-2">
                <a
                  href={waHref}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-5 py-3 rounded-sm font-body font-bold text-xs uppercase tracking-[0.2em]"
                  data-testid="nav-wa-mobile"
                >
                  <MessageCircle size={14} /> {t("wa.label")}
                </a>
                <a
                  href="tel:8193503027"
                  className="inline-flex items-center justify-center gap-2 bg-[#E87B3A] text-white px-5 py-3 rounded-sm font-body font-bold text-xs uppercase tracking-[0.2em]"
                  data-testid="nav-cta-phone-mobile"
                >
                  <Phone size={14} /> 819-350-3027
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
